/**
 * Web-only connect-failure classification (ENG-4331).
 *
 * The desktop build's LiveKit/WebRTC failures already reach PostHog Error
 * Tracking as named `studio:<subsystem>:<issue>` groups with a human title and
 * description (see GROUPING_RULES in noise-filter.ts). The standalone-web build
 * has no equivalent: its connect failures either fall into the unclassified
 * fallback bucket (one fragmented issue per stack hash) or are dropped outright
 * by the teardown-noise suppression rules, so a browser user who never gets
 * video is invisible in the dashboards.
 *
 * This module is the pure, directly testable half of the fix: given an
 * exception's client, type and message it returns the canonical group the
 * failure belongs to, or `null` when the failure is not a web connect failure.
 * It performs no capture, holds no state, and never throws — the caller
 * (noise-filter.ts) owns rate limiting and property stamping.
 *
 * Two deliberate constraints:
 *
 *  1. Group identity is COMPOSED from typed parts, never hand-written, so an
 *     invalid (subsystem, issue) pair fails `tsc` instead of quietly minting a
 *     new PostHog issue. Same contract as `eventName` in
 *     analytics/constants/event-names.ts.
 *  2. Where the browser hits a failure the desktop already names, this reuses
 *     the desktop group's EXACT fingerprint. Web and desktop then land in one
 *     PostHog issue that `client` splits, which is what lets a single consumer
 *     read both, and means no existing saved insight changes shape.
 *
 * Third-party protocol names (LiveKitError, UnexpectedConnectionState,
 * DOMException and the SDK message text they carry) stay literals here: they
 * are not our namespace and we do not control their spelling.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 * Per rule: think-in-systems (ai.knowledge/global) — classification is one pure
 * function with one owner, not logic smeared across the error handlers.
 */

import { COMMON_PROPERTIES, ENVELOPE_CLIENT } from '#~/analytics/constants/index.js';

// ─── Typed group identity ──────────────────────────────────────────────────

/**
 * Namespace prefix every Studio error group shares. Extracted as a constant
 * because both the fingerprint builder and `subsystemFromFingerprint` in
 * noise-filter.ts depend on the `studio:<subsystem>:<issue>` shape holding.
 */
export const ERROR_GROUP_NAMESPACE = 'studio';

/** Subsystem segment of the fingerprint — the same values desktop groups use. */
export const CONNECT_FAILURE_SUBSYSTEM = {
  LIVEKIT: 'livekit',
  WEBRTC: 'webrtc'
} as const;

export type ConnectFailureSubsystem =
  (typeof CONNECT_FAILURE_SUBSYSTEM)[keyof typeof CONNECT_FAILURE_SUBSYSTEM];

/**
 * Issue segment of the fingerprint. This token is ALSO the value published on
 * the `connect_failure_kind` property, so the queryable cohort and the group
 * name are literally the same string — there is no second mapping table to
 * drift out of sync.
 *
 * SIGNAL_CONNECTION_FAILED / PUBLISHER_CONNECTION_CLOSED / NEGOTIATION_TIMEOUT
 * are pre-existing desktop group names and are reused verbatim on purpose.
 */
export const CONNECT_FAILURE_ISSUE = {
  SIGNAL_CONNECTION_FAILED: 'signal-connection-failed',
  PUBLISHER_CONNECTION_CLOSED: 'publisher-connection-closed',
  NEGOTIATION_TIMEOUT: 'negotiation-timeout',
  CONNECT_TIMEOUT: 'connect-timeout',
  UNAUTHORIZED: 'unauthorized',
  CONNECTION_STATE_UNEXPECTED: 'connection-state-unexpected',
  PEER_CONNECTION_CLOSED: 'peer-connection-closed',
  MEDIA_PERMISSION_DENIED: 'media-permission-denied',
  MEDIA_DEVICE_UNAVAILABLE: 'media-device-unavailable'
} as const;

export type ConnectFailureIssue =
  (typeof CONNECT_FAILURE_ISSUE)[keyof typeof CONNECT_FAILURE_ISSUE];

/**
 * Subsystem → the issues that may legally compose with it. `satisfies` keeps
 * the map total over ConnectFailureSubsystem (adding a subsystem without
 * declaring its issues is a compile error) without widening the per-subsystem
 * tuples, so `IssueFor<S>` stays a literal union.
 */
export const CONNECT_FAILURE_ISSUE_MATRIX = {
  [CONNECT_FAILURE_SUBSYSTEM.LIVEKIT]: [
    CONNECT_FAILURE_ISSUE.SIGNAL_CONNECTION_FAILED,
    CONNECT_FAILURE_ISSUE.PUBLISHER_CONNECTION_CLOSED,
    CONNECT_FAILURE_ISSUE.NEGOTIATION_TIMEOUT,
    CONNECT_FAILURE_ISSUE.CONNECT_TIMEOUT,
    CONNECT_FAILURE_ISSUE.UNAUTHORIZED,
    CONNECT_FAILURE_ISSUE.CONNECTION_STATE_UNEXPECTED
  ],
  [CONNECT_FAILURE_SUBSYSTEM.WEBRTC]: [
    CONNECT_FAILURE_ISSUE.PEER_CONNECTION_CLOSED,
    CONNECT_FAILURE_ISSUE.MEDIA_PERMISSION_DENIED,
    CONNECT_FAILURE_ISSUE.MEDIA_DEVICE_UNAVAILABLE
  ]
} as const satisfies Record<ConnectFailureSubsystem, readonly ConnectFailureIssue[]>;

/** The issues the matrix sanctions for a given subsystem. */
export type IssueFor<S extends ConnectFailureSubsystem> =
  (typeof CONNECT_FAILURE_ISSUE_MATRIX)[S][number];

/** Every fingerprint this module can mint, as a literal union. */
export type ConnectFailureFingerprint = {
  [S in ConnectFailureSubsystem]: `${typeof ERROR_GROUP_NAMESPACE}:${S}:${IssueFor<S>}`;
}[ConnectFailureSubsystem];

/**
 * Compose a group fingerprint from typed parts. The return type is the template
 * literal `studio:${S}:${I}`, so call sites keep literal-typed constants and an
 * off-matrix (subsystem, issue) pair is rejected by the `IssueFor<S>`
 * constraint at compile time.
 */
export const connectFailureFingerprint = <S extends ConnectFailureSubsystem, I extends IssueFor<S>>(
  subsystem: S,
  issue: I
): `${typeof ERROR_GROUP_NAMESPACE}:${S}:${I}` => `${ERROR_GROUP_NAMESPACE}:${subsystem}:${issue}`;

/**
 * Property keys this classification publishes. Shared so app code and tests
 * read the same source and a rename cannot silently pass its own test.
 */
export const CONNECT_FAILURE_PROPERTIES = {
  /** The issue token — same string as the fingerprint's third segment. */
  KIND: 'connect_failure_kind',
  /** The subsystem token — same string as the fingerprint's second segment. */
  SUBSYSTEM: 'subsystem'
} as const;

// ─── Product tags on a named group ─────────────────────────────────────────
//
// A named group is only half of "web users are visible on failure surfaces".
// The other half is the tags a consumer FILTERS on: this program mandates
// filtering failure surfaces by the `severity` property rather than by event or
// group name, precisely so an EXPECTED signal (discovery_empty at S3/warn) can
// be excluded without maintaining a name list. A web connect-failure group that
// carries no severity is therefore invisible to every such surface — the exact
// outcome naming these groups exists to prevent — even though the group exists.
//
// A sibling desktop exception catalog, keyed on the same fingerprints, is being
// introduced at analytics/constants/exception-catalog.ts. Where that catalog and
// this table describe the same fingerprint they must agree, and the forward guard
// in __tests__/web-connect-failures.test.ts fails the moment that module exists
// and disagrees — see that test for why the vocabulary is declared here rather
// than widened in analytics/constants/event-properties.ts.

/**
 * The one severity tier a connect failure can carry.
 *
 * DELIBERATELY A SINGLETON, AND `S3/warn` IS DELIBERATELY NOT IN IT. S3/warn is
 * not a softer label in this program, it is an exclusion instruction: alert rules
 * and funnels are told to drop S3/warn because that tier means "EXPECTED, not a
 * failure" (discovery_empty, room teardown). Routing a real connect failure there
 * would not mislabel it, it would delete it from the numbers — silently, with
 * nothing going red. Since every row in this table is connect-phase, i.e. the
 * person got no video, a non-blocking connect failure is a contradiction, and the
 * safe shape is for the tier not to be reachable from here at all rather than to
 * be reachable and merely unused.
 *
 * `S2/error` is not in the shared EVENT_SEVERITY map yet — the branch introducing
 * exception-catalog.ts adds it there alongside `S1/fatal` — and it is declared here
 * with that branch's EXACT spelling rather than added to event-properties.ts,
 * because that file is under concurrent edit by many in-flight branches and a
 * further writer on it for two tokens buys a conflict in the most contended shared
 * file for no behavioural gain. The forward guard in the test suite collapses this
 * once the shared tier lands.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const CONNECT_FAILURE_SEVERITY = {
  S2_ERROR: 'S2/error'
} as const;

export type ConnectFailureSeverity =
  (typeof CONNECT_FAILURE_SEVERITY)[keyof typeof CONNECT_FAILURE_SEVERITY];

/**
 * Property keys the product tags are published under. `SEVERITY` already exists
 * in the shared map and is read from there; the other three carry the exception
 * catalog's spelling for the same reason as CONNECT_FAILURE_SEVERITY above.
 */
export const CONNECT_FAILURE_TAG_PROPERTIES = {
  SEVERITY: COMMON_PROPERTIES.SEVERITY,
  BLOCKING: 'blocking',
  ERROR_WHAT: 'error_what',
  ERROR_WHY: 'error_why'
} as const;

/** The tag stamp for one named group: what/why plus what alerting filters on. */
export type ConnectFailureProductTags = Readonly<{
  [CONNECT_FAILURE_TAG_PROPERTIES.SEVERITY]: ConnectFailureSeverity;
  [CONNECT_FAILURE_TAG_PROPERTIES.BLOCKING]: boolean;
  [CONNECT_FAILURE_TAG_PROPERTIES.ERROR_WHAT]: string;
  [CONNECT_FAILURE_TAG_PROPERTIES.ERROR_WHY]: string;
}>;

// ─── Classification ────────────────────────────────────────────────────────

/**
 * What the classifier needs from an exception. Values are `unknown` because
 * they arrive from a PostHog property bag that anything can write into; the
 * classifier narrows rather than trusting the caller.
 */
export interface WebConnectFailureInput {
  client?: unknown;
  type?: unknown;
  message?: unknown;
}

/** A named group plus the human-facing text PostHog Issues renders. */
export interface WebConnectFailureClassification {
  subsystem: ConnectFailureSubsystem;
  issue: ConnectFailureIssue;
  fingerprint: ConnectFailureFingerprint;
  title: string;
  description: string;
  severity: ConnectFailureSeverity;
  blocking: boolean;
}

/** True only for the standalone browser build's envelope discriminator. */
export function isStandaloneWebClient(client: unknown): boolean {
  return client === ENVELOPE_CLIENT.STANDALONE_WEB;
}

/** The hand-authored half of a rule; the fingerprint is derived, never typed in. */
interface ConnectFailureRuleSpec<S extends ConnectFailureSubsystem, I extends IssueFor<S>> {
  subsystem: S;
  issue: I;
  title: string;
  description: string;
  /**
   * Required, not defaulted: a new rule must state the tier it belongs to rather
   * than silently inheriting a blanket one, because that tier is what decides
   * whether an alert fires on it.
   */
  severity: ConnectFailureSeverity;
  /** Does this failure block the new-user happy path (no video for the user)? */
  blocking: boolean;
  /** Third-party error name this rule applies to; omitted means any name. */
  type?: RegExp;
  /** Connect-phase message shape. Required — a bare type match is too broad. */
  message: RegExp;
}

/**
 * Attach the composed fingerprint to a rule while preserving both literal
 * parameters. Going through the generic builder is what makes the rule table
 * self-validating: an (subsystem, issue) pair the matrix does not sanction
 * fails `tsc` at the call site below, and the classifier can return
 * `rule.fingerprint` with no cast because its type is already the literal.
 */
const connectFailureRule = <S extends ConnectFailureSubsystem, I extends IssueFor<S>>(
  spec: ConnectFailureRuleSpec<S, I>
): ConnectFailureRuleSpec<S, I> & { fingerprint: `${typeof ERROR_GROUP_NAMESPACE}:${S}:${I}` } => ({
  ...spec,
  fingerprint: connectFailureFingerprint(spec.subsystem, spec.issue)
});

/**
 * Ordered rules, first match wins. Every entry is a CONNECT-phase shape:
 * teardown and React-lifecycle shapes (metadata-update timeout, "unmounting
 * before the effect has completed", AbortError) are intentionally absent so
 * they keep flowing to the existing suppression rules on web exactly as they do
 * on desktop. Widening this table is what turns a dropped teardown log into
 * dashboard noise, so it stays narrow.
 *
 * Every row is `S2/error` and blocking, and that is the consequence of the table
 * being connect-phase only: each of these shapes means the browser never joined
 * the room or never opened local media, so the person watching gets no video.
 * The tier is still stated per row rather than applied in bulk, so a future rule
 * has to make the call explicitly instead of inheriting one.
 */
const CONNECT_FAILURE_RULES = [
  // LiveKit could not reach the signaling service. Desktop already groups the
  // `ConnectionError` spelling of this under the same fingerprint; the browser
  // SDK surfaces it as LiveKitError, which previously fell through to the
  // unclassified fallback and fragmented by stack hash.
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.LIVEKIT,
    issue: CONNECT_FAILURE_ISSUE.SIGNAL_CONNECTION_FAILED,
    type: /livekiterror|connectionerror/i,
    message: /could not establish signal connection/i,
    title: 'LiveKit signal connection failed',
    description: 'LiveKit could not reach the signaling service during connection setup.',
    // Reused desktop fingerprint: severity and blocking must match the exception
    // catalog's row for studio:livekit:signal-connection-failed, or the same
    // PostHog issue would carry contradictory tags split by `client`.
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.LIVEKIT,
    issue: CONNECT_FAILURE_ISSUE.PUBLISHER_CONNECTION_CLOSED,
    type: /livekiterror|connectionerror/i,
    message: /could not establish (publisher|pc) connection/i,
    title: 'LiveKit publisher connection closed',
    description: 'The publisher peer connection closed while LiveKit was starting media publish.',
    // Reused desktop fingerprint — see the signal-connection-failed note.
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.LIVEKIT,
    issue: CONNECT_FAILURE_ISSUE.NEGOTIATION_TIMEOUT,
    type: /livekiterror|connectionerror/i,
    message: /negotiation timed out/i,
    title: 'LiveKit negotiation timed out',
    description: 'LiveKit media negotiation did not complete before the timeout.',
    // Reused desktop fingerprint — see the signal-connection-failed note.
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.LIVEKIT,
    issue: CONNECT_FAILURE_ISSUE.CONNECT_TIMEOUT,
    type: /livekiterror|connectionerror/i,
    message: /(^|\W)(connect|connection) (attempt )?timed out/i,
    title: 'LiveKit connect timed out',
    description: 'The browser did not complete the LiveKit room join before the connect timeout.',
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.LIVEKIT,
    issue: CONNECT_FAILURE_ISSUE.UNAUTHORIZED,
    type: /livekiterror|connectionerror/i,
    message: /unauthorized|invalid token|token (is )?expired|permission denied to join/i,
    title: 'LiveKit join unauthorized',
    description: 'The LiveKit server rejected the room token, so the browser never joined.',
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  // The SDK reached a state it does not expect mid-connect. On desktop this is
  // suppressed as teardown noise; in the browser it is the shape a user sees
  // when the transport dies during join, which is exactly the failure this
  // ticket exists to make visible.
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.LIVEKIT,
    issue: CONNECT_FAILURE_ISSUE.CONNECTION_STATE_UNEXPECTED,
    type: /unexpectedconnectionstate/i,
    message: /pc manager is closed|unexpected connection state/i,
    title: 'LiveKit unexpected connection state',
    description:
      'LiveKit reached an unexpected connection state while the browser was joining the room.',
    // Deliberately NOT the desktop tier. The desktop group for the same physical
    // error (studio:livekit:pc-manager-closed) is catalogued as normal room
    // teardown at S3/warn and non-blocking, which is the correct reading there —
    // the desktop app has already left the room. In the browser this is the shape
    // a user sees when the transport dies mid-join, so it is a blocking failure.
    // The two readings live in two fingerprints on purpose; blending them would
    // hide real web failures inside a group alerting is told to ignore.
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  // WebRTC operations issued against an already-closed peer connection.
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.WEBRTC,
    issue: CONNECT_FAILURE_ISSUE.PEER_CONNECTION_CLOSED,
    type: /domexception|invalidstateerror/i,
    message:
      /(addicecandidate|setremotedescription|setlocaldescription|createanswer|createoffer)[\s\S]*clos(ed|ing)/i,
    title: 'WebRTC peer connection closed',
    description: 'A WebRTC negotiation call ran against a peer connection that was already closed.',
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  // Browser-only: the desktop build resolves camera and microphone access
  // through the native permission flow, so these two shapes cannot occur there.
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.WEBRTC,
    issue: CONNECT_FAILURE_ISSUE.MEDIA_PERMISSION_DENIED,
    type: /domexception|notallowederror|securityerror/i,
    message: /permission (denied|dismissed)|notallowederror/i,
    title: 'WebRTC media permission denied',
    description: 'The browser denied camera or microphone access, so no local media could start.',
    // Blocking even though the user's own choice can cause it: the happy path
    // needs local media, so from the funnel's point of view the journey stopped
    // here. The tag says "blocked", not "our bug".
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  }),
  connectFailureRule({
    subsystem: CONNECT_FAILURE_SUBSYSTEM.WEBRTC,
    issue: CONNECT_FAILURE_ISSUE.MEDIA_DEVICE_UNAVAILABLE,
    type: /domexception|notfounderror|notreadableerror|overconstrainederror/i,
    message:
      /(requested device not found|notfounderror|notreadableerror|could not start (video|audio) source|overconstrained)/i,
    title: 'WebRTC media device unavailable',
    description:
      'The browser could not open the requested camera or microphone, so no local media could start.',
    severity: CONNECT_FAILURE_SEVERITY.S2_ERROR,
    blocking: true
  })
] as const;

/**
 * Fingerprint → product tags, built from the rule table so the tags and the
 * group name can only ever come from the same row.
 *
 * Keyed on the fingerprint rather than on the classification object because the
 * consumer that stamps these (the exit funnel in noise-filter.ts) sees only the
 * finished filter result. That is deliberate: it means ANY return path that ends
 * up naming one of these groups gets the tags, including a path added later that
 * knows nothing about this module.
 */
const CONNECT_FAILURE_TAGS_BY_FINGERPRINT: ReadonlyMap<string, ConnectFailureProductTags> = new Map(
  CONNECT_FAILURE_RULES.map((rule) => [
    rule.fingerprint,
    {
      [CONNECT_FAILURE_TAG_PROPERTIES.SEVERITY]: rule.severity,
      [CONNECT_FAILURE_TAG_PROPERTIES.BLOCKING]: rule.blocking,
      [CONNECT_FAILURE_TAG_PROPERTIES.ERROR_WHAT]: rule.title,
      [CONNECT_FAILURE_TAG_PROPERTIES.ERROR_WHY]: rule.description
    }
  ])
);

/**
 * The product tags for a named connect-failure group, or `undefined` when the
 * fingerprint is not one this module owns.
 *
 * Accepts `unknown` and tolerates the array form because `$exception_fingerprint`
 * is allowed to be `string | string[]` at the send boundary; an unrecognised
 * value returns undefined so the event still ships, just untagged.
 */
export function connectFailureProductTags(
  fingerprint: unknown
): ConnectFailureProductTags | undefined {
  const key = Array.isArray(fingerprint) ? <unknown>fingerprint[0] : fingerprint;

  return typeof key === 'string' ? CONNECT_FAILURE_TAGS_BY_FINGERPRINT.get(key) : undefined;
}

/** Narrow an `unknown` property value to a usable, trimmed string. */
function readableText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Classify a standalone-web LiveKit/WebRTC connect failure.
 *
 * Returns `null` — never throws, never partially classifies — when the client
 * is not the browser build, when either field is missing or non-string, or when
 * nothing in the rule table matches. `null` means "not mine": the caller leaves
 * the exception on its existing path, so an unknown error degrades into the
 * pipeline's normal unclassified handling rather than being swallowed.
 */
export function classifyWebConnectFailure(
  input: WebConnectFailureInput
): WebConnectFailureClassification | null {
  if (!isStandaloneWebClient(input.client)) return null;

  const type = readableText(input.type);
  const message = readableText(input.message);
  // Both halves are required: a bare type match would sweep in teardown noise,
  // and a bare message match would claim errors from unrelated subsystems.
  if (!type || !message) return null;

  for (const rule of CONNECT_FAILURE_RULES) {
    if (rule.type && !rule.type.test(type)) continue;
    if (!rule.message.test(message)) continue;

    // Every field is read straight off the rule, including the fingerprint the
    // generic builder already composed and literal-typed at authoring time —
    // recomposing it here would need a cast and would let the returned group
    // drift from the one the matrix sanctioned.
    return {
      subsystem: rule.subsystem,
      issue: rule.issue,
      fingerprint: rule.fingerprint,
      title: rule.title,
      description: rule.description,
      severity: rule.severity,
      blocking: rule.blocking
    };
  }

  return null;
}

/** Exposed for tests that assert the table stays narrow and fully covered. */
export { CONNECT_FAILURE_RULES };
